import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import { withStyles } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import UnsubscribeIcon from "@mui/icons-material/Unsubscribe";
import DownloadIcon from "@mui/icons-material/Download";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import dayjs from "dayjs";
import { messageShow } from "../../common/api/actions";
import EmptyMessage from "../../common/EmptyMessage";
import BrandedLoading from "../../common/BrandedLoading";
import Pagination from "../../common/Pagination";
import SectionPaper from "../../common/SectionPaper";
import { getPlatformUsers } from "../api/actions/query";
import { deleteUser } from "../../user/api/actions/mutation";
import routes from "../api/routes";

const styles = (theme) => ({
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 3),
  },
  searchFieldsAll: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  tableContainer: {
    overflowX: "auto",
  },
});

class EngagementUsersUnsubscribed extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      users: [],
      count: 0,
      page: 1,
      limit: 20,
      searchField: "all",
      searchQuery: "",
      deleteConfirmOpen: false,
      userToDelete: null,
    };
  }

  componentDidMount() {
    this.fetchUsers(1);
  }

  fetchUsers = async (page = 1) => {
    const { getPlatformUsers, messageShow } = this.props;
    const { limit, searchField, searchQuery } = this.state;

    this.setState({ isLoading: true });

    try {
      const response = await getPlatformUsers({
        page,
        limit,
        searchQuery,
        searchField,
        emailSubscription: false,
      });

      if (response && response.success && response.data) {
        const userList = Array.isArray(response.data.users)
          ? response.data.users
          : Array.isArray(response.data.list)
          ? response.data.list
          : Array.isArray(response.data)
          ? response.data
          : [];

        const totalCount =
          typeof response.data.count === "number"
            ? response.data.count
            : response.data.pagination?.total || userList.length;

        this.setState({
          users: userList,
          count: totalCount,
          page,
        });
      } else {
        this.setState({ users: [], count: 0 });
        if (response && response.message) {
          messageShow(response.message);
        }
      }
    } catch (error) {
      console.error("Error fetching unsubscribed engagement users:", error);
      messageShow("Failed to load unsubscribed engagement users.");
      this.setState({ users: [], count: 0 });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handlePageChange = (page) => {
    this.setState({ page }, () => this.fetchUsers(page));
  };

  handleSearch = () => {
    this.setState({ page: 1 }, () => this.fetchUsers(1));
  };

  handleClearSearch = () => {
    this.setState({ searchQuery: "", searchField: "all", page: 1 }, () => {
      this.fetchUsers(1);
    });
  };

  handleOpenDeleteConfirm = (user) => {
    this.setState({
      deleteConfirmOpen: true,
      userToDelete: user,
    });
  };

  handleCloseDeleteConfirm = () => {
    this.setState({
      deleteConfirmOpen: false,
      userToDelete: null,
    });
  };

  handleConfirmDelete = async () => {
    const { userToDelete, page } = this.state;
    if (!userToDelete) return;

    const { deleteUser, messageShow } = this.props;

    try {
      const { data } = await deleteUser({ userId: userToDelete._id });
      messageShow(data?.message || "User soft-deleted successfully.");
      if (data?.success) {
        this.handleCloseDeleteConfirm();
        this.fetchUsers(page);
      } else {
        this.handleCloseDeleteConfirm();
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      messageShow("Some error occurred while deleting user.");
      this.handleCloseDeleteConfirm();
    }
  };

  exportCSV = () => {
    const { users } = this.state;
    const userList = Array.isArray(users) ? users : [];
    if (!userList || userList.length === 0) return;

    const headers = ["Name", "Email", "Mobile", "Role", "Joined Date", "Email Status"];
    const rows = userList.map((u) => [
      `"${u.name || ""}"`,
      `"${u.email || ""}"`,
      `"${u.mobile || ""}"`,
      `"${u.role || ""}"`,
      `"${u.createdAt ? dayjs(u.createdAt).format("YYYY-MM-DD HH:mm") : ""}"`,
      `"Unsubscribed"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `engagement_users_email_unsubscribed_list_${dayjs().format("YYYY-MM-DD")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  renderRoleBadge = (role) => {
    const label = role ? role.charAt(0).toUpperCase() + role.slice(1) : "Unknown";
    return (
      <Chip
        label={label}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          borderColor: "#cbd5e1",
          color: "#0d3559",
        }}
      />
    );
  };

  render() {
    const { classes, history } = this.props;
    const {
      users,
      isLoading,
      count,
      page,
      limit,
      searchField,
      searchQuery,
      deleteConfirmOpen,
      userToDelete,
    } = this.state;

    const userList = Array.isArray(users) ? users : [];

    return (
      <div style={{ padding: "20px" }}>
        {/* Top Header with Back Button */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push(routes.engagementUsers.path(1))}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                borderColor: "#cbd5e1",
                color: "#475569",
                fontWeight: 600,
                "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
              }}
            >
              Back to Users
            </Button>
            <Typography variant="h5" style={{ fontWeight: 800, color: "#0d3559" }}>
              Email Unsubscribed List — Engagement Users
            </Typography>
            <Chip
              size="small"
              label={`${count} Unsubscribed`}
              sx={{
                bgcolor: "#fee2e2",
                color: "#dc2626",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={this.exportCSV}
            disabled={userList.length === 0}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Export Unsubscribed CSV
          </Button>
        </Box>

        {/* Regulatory & Compliance Notice */}
        <Box
          sx={{
            p: 2,
            bgcolor: "#fef2f2",
            border: "1px solid #fee2e2",
            borderRadius: "12px",
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <UnsubscribeIcon sx={{ color: "#ef4444", fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#991b1b" }}>
              Engagement Email Unsubscribed Registry
            </Typography>
            <Typography variant="body2" sx={{ color: "#b91c1c", fontSize: "0.85rem" }}>
              In accordance with CAN-SPAM and GDPR regulations, platform and engagement users listed here have explicitly opted out of email communications. Automated outreach and broadcast campaigns will not deliver to these addresses.
            </Typography>
          </Box>
        </Box>

        {/* Search Toolbar */}
        <Toolbar className={classes.toolbar} style={{ padding: 0, marginBottom: "16px" }}>
          <div className={classes.searchFieldsAll}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                  <InputLabel id="search-field-label">Search By</InputLabel>
                  <Select
                    labelId="search-field-label"
                    value={searchField}
                    onChange={(e) => this.setState({ searchField: e.target.value })}
                    label="Search By"
                    style={{ borderRadius: "8px" }}
                  >
                    <MenuItem value="all">All Fields</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="role">Role</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Search unsubscribed user..."
                  value={searchQuery}
                  onChange={(e) => this.setState({ searchQuery: e.target.value })}
                  style={{ minWidth: 260 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={this.handleSearch}
                  sx={{
                    bgcolor: "#0d3559",
                    color: "white",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#092a46", boxShadow: "none" },
                  }}
                >
                  Search
                </Button>
              </Grid>
              {searchQuery && (
                <Grid item>
                  <Button
                    variant="text"
                    onClick={this.handleClearSearch}
                    sx={{ textTransform: "none", color: "#64748b" }}
                  >
                    Clear Filter
                  </Button>
                </Grid>
              )}
            </Grid>
          </div>
        </Toolbar>

        {/* Table View */}
        <SectionPaper>
          {isLoading ? (
            <BrandedLoading />
          ) : userList.length === 0 ? (
            <EmptyMessage message="No unsubscribed users found." />
          ) : (
            <>
              <div className={classes.tableContainer}>
                <Table padding="normal">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 700, color: "#334155" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#334155" }}>Email ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#334155" }}>Mobile</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#334155" }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#334155" }}>Joined Date</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                        Email Status
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userList.map((user) => (
                      <TableRow key={user._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#0d3559" }}>
                            {user.name || "Unnamed"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{user.email || "—"}</Typography>
                        </TableCell>
                        <TableCell>{user.mobile || "—"}</TableCell>
                        <TableCell>{this.renderRoleBadge(user.role)}</TableCell>
                        <TableCell>
                          {user.createdAt ? dayjs(user.createdAt).format("DD MMM YYYY") : "—"}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label="UNSUBSCRIBED"
                            sx={{
                              bgcolor: "#fee2e2",
                              color: "#dc2626",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              letterSpacing: "0.5px",
                              borderRadius: "6px",
                              height: 22,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Soft Delete User">
                            <IconButton
                              size="small"
                              onClick={() => this.handleOpenDeleteConfirm(user)}
                              sx={{
                                color: "#ef4444",
                                "&:hover": { bgcolor: "#fee2e2" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Pagination
                  count={count}
                  page={page}
                  limit={limit}
                  onChange={this.handlePageChange}
                />
              </Box>
            </>
          )}
        </SectionPaper>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteConfirmOpen} onClose={this.handleCloseDeleteConfirm}>
          <DialogTitle sx={{ fontWeight: 700, color: "#0d3559" }}>Confirm Soft Delete</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "#475569" }}>
              Are you sure you want to soft delete{" "}
              <strong>{userToDelete?.name || userToDelete?.email || "this user"}</strong>?
              <br />
              This will safely mark the record as deleted (<code>isDeleted: true</code>) without breaking history.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={this.handleCloseDeleteConfirm} sx={{ textTransform: "none", color: "#64748b" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={this.handleConfirmDelete}
              sx={{
                bgcolor: "#dc2626",
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
              }}
            >
              Soft Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

EngagementUsersUnsubscribed.propTypes = {
  classes: PropTypes.object.isRequired,
  getPlatformUsers: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired,
  messageShow: PropTypes.func.isRequired,
};

export default withRouter(
  connect(null, { getPlatformUsers, deleteUser, messageShow })(
    withStyles(styles)(EngagementUsersUnsubscribed)
  )
);
